import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string-for-all';
import { AppContext } from '@prisjakt/ui-app-context';
import {
  DropdownNew as Dropdown,
  ListItem as DropdownItem,
} from 'src/components/dropdown-new';
import translator from '@prisjakt/ui-translate/src/translator';
import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';
import { getSortedQuery } from '@prisjakt/product-collection-urls';
import { getPropertyFromSortKey } from '../../utils/properties';

import * as S from './styles';

type Props = {
  availableSorts: string[];
  availableSortsTranslations?: Record<string, string>;
  currentSort: string;
  onClick?: (sort: string) => void;
};

export const getTranslationForSortOption = (
  sortKey: string,
  availableTranslations: Record<string, string>,
  t: ReturnType<typeof translator>
): string => {
  const property = getPropertyFromSortKey(sortKey);
  return availableTranslations[property]
    ? availableTranslations[property]
    : t(
        'common',
        `sort.${
          availableTranslations[sortKey]
            ? availableTranslations[sortKey]
            : sortKey
        }` as keyof ParamsAndReturnTypes['common']
      );
};

const SortOptions = ({
  availableSorts,
  availableSortsTranslations = {},
  currentSort,
  onClick,
}: Props) => {
  const { t } = useContext(AppContext);
  const location = useLocation();
  const sortOptions = Array.isArray(availableSorts) ? availableSorts : [];
  const currentSortName = getTranslationForSortOption(
    currentSort,
    availableSortsTranslations,
    t
  );
  if (!sortOptions.length) {
    return null;
  }

  return (
    <S.SortOptions>
      <S.SortOptionsTitle>{t('common', 'sort.title')}:</S.SortOptionsTitle>

      <Dropdown
        variant="secondary"
        showArrow
        grow="right"
        toggleElement={
          <S.SortOptionsButton variant="text" color="primary">
            {currentSortName}
          </S.SortOptionsButton>
        }
        data-test="SortOrderDropdown"
      >
        {sortOptions
          .filter(sortOption => sortOption !== currentSort)
          .map(sort => {
            const queryParams = getSortedQuery({
              ...queryString.parse(location.search.replace(/^\?/, '')),
              offset: undefined,
              direction: undefined,
              sort,
            });

            return (
              <DropdownItem
                key={sort}
                to={`${location.pathname}?${queryString.stringify(
                  queryParams
                )}`}
                onClick={() => (onClick ? onClick(sort) : null)}
              >
                {getTranslationForSortOption(
                  sort,
                  availableSortsTranslations,
                  t
                )}
              </DropdownItem>
            );
          })}
      </Dropdown>
    </S.SortOptions>
  );
};

export default SortOptions;
